import React, { Fragment, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pMap from 'p-map-browser';

import {
  CARD_ELEMENT_TYPE,
  CARD_SIDE,
  ORIENTATION,
} from '../../../../cards/constants';
import usePrintProductConfiguration from '../../PrintProductConfigurator/usePrintProductConfiguration';
import DesignEditorLoadingIndicator from '../../../../../components/global/DesignEditor/components/DesignEditorLoadingIndicator';
import AccountCard from '../../../../cards/components/AccountCard';
import NativeActivityIndicator from '../../../../../components/global/NativeActivityIndicator';
import useOpenAlert from '../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../util/normalize-errors';
import PrintBackgroundSelectorModal from '../../PrintBackgroundSelector';
import {
  getAccountCardElementsConfiguration,
  getCardTemplateSizing,
  isCardElementEditable,
} from '../../../../cards/components/AccountCard/helpers';
import { isElementCardBackground } from '../../../../../components/global/CardDesigner/components/CardDesignerControlPanel/components/CardDesignerBackgroundControls/helpers';
import { TEXT_PRESETS } from '../../../../../components/global/CardDesigner/components/CardDesignerCardElements/CardDesignerCardElementText/helpers';
import { getTextElementPresetData } from '../../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/helpers';
import { getAutoFitPositionPhotoCropbox } from '../../../../cards/components/CardElementPhoto/helpers';
import { getMaxThumb } from '../../../../../util/photo';
import useIdentityJerseyColors from '../../../../../apps/pro/views/HierarchyItem/Matches/hooks/useIdentityJerseyColors';

const propTypes = {
  printProductId: PropTypes.string,
  printProductSizeId: PropTypes.string,
  orientation: PropTypes.oneOf(Object.values(ORIENTATION)),
  defaultAccountCard: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    is_album: PropTypes.bool,
  }),
  identity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  photo: PropTypes.shape({
    id: PropTypes.string,
  }),
  onAccountCardUpdated: PropTypes.func,
  printBackgrounds: PropTypes.arrayOf({
    id: PropTypes.string,
    asset_image: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  printBackgroundModalOpen: PropTypes.bool,
  setPrintBackgroundModalOpen: PropTypes.func,
  allowPhotoChange: PropTypes.bool,
  allowMultiIdentityPhoto: PropTypes.bool,
  className: PropTypes.string,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
    watermarkMode: PropTypes.string,
    wallpaperWatermarkMode: PropTypes.string,
  }),
};

const PRESET_FILL_STATUS = {
  LOADING: 'LOADING',
  FILLING: 'FILLING',
  FILLED: 'FILLED',
};

const PrintProductGroupConfigurationItem = ({
  printProductId,
  printProductSizeId,
  printProductSizeServiceIds,
  orientation,
  defaultAccountCard,
  albumMembershipGroup,
  identity,
  photo,
  onAccountCardUpdated,
  printBackgrounds,
  printBackgroundModalOpen,
  setPrintBackgroundModalOpen,
  allowPhotoChange,
  allowMultiIdentityPhoto,
  className,
  watermarkSettings,
}) => {
  const openAlert = useOpenAlert();
  const [
    accountCardElementReferences,
    setAccountCardElementReferences,
  ] = useState({});
  const [presetFillStatus, setPresetFillStatus] = useState(
    PRESET_FILL_STATUS.LOADING,
  );

  const {
    printProduct,
    printProductConfiguration: {
      selectedPrintProductSize,
      handlePrintProductSizeSelect,
    },
    accountCardConfiguration: {
      loadingAccountCard,
      accountCard,
      onElementConfigurationChange,
      cardElementLoading,
      cardSides,
    },
  } = usePrintProductConfiguration({
    printProductId,
    defaultPrintProductSizeId: printProductSizeId,
    defaultPrintProductSizeServiceIds: printProductSizeServiceIds,
    defaultPhoto: photo,
    defaultAccountCardId: defaultAccountCard?.id,
    defaultOrientation: orientation,
    onError: error => {
      openAlert({
        type: 'error',
        message:
          'It looks like an error ocurred while we were trying to configure your prints. Please start the configuration over.',
        info: getErrorString(error),
      });
    },
  });
  const { teamName, levelName } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId: albumMembershipGroup?.is_album
      ? albumMembershipGroup?.album?.root_album_folder?.id
      : albumMembershipGroup?.id,
  });

  const state = useMemo(
    () => getAccountCardElementsConfiguration(accountCard, cardElementLoading),
    [accountCard, cardElementLoading],
  );

  const backgroundElement = useMemo(
    () =>
      state.find(e => e.side === CARD_SIDE.FRONT && isElementCardBackground(e)),
    [state],
  );

  useEffect(() => {
    if (selectedPrintProductSize?.id) {
      onAccountCardUpdated({
        printProduct,
        printProductSize: selectedPrintProductSize,
        accountCard,
        accountCardElementReferences,
      });
    }
  }, [
    printProduct?.id,
    selectedPrintProductSize?.id,
    accountCard?.id,
    // Listen to changes within account_card_sides
    // users should have valid configurations, hence why we
    // should update the account card sides whenever possible.
    accountCard?.account_card_sides,
    backgroundElement,
    accountCardElementReferences,
  ]);

  useEffect(() => {
    const setBackground = async () => {
      const photoPrintBackground = photo?.print_background;

      if (
        printBackgrounds?.length > 0 &&
        !backgroundElement?.cardElementConfiguration?.asset_image?.id
      ) {
        const isPrintBackdropInPackage = !!printBackgrounds?.find(
          ({ id }) => id === photoPrintBackground?.id,
        );
        await onElementConfigurationChange(backgroundElement, {
          photo_id: null,
          // We need to keep the element non-editable for it to work properly.
          is_editable: false,
          existing_asset_image_id: isPrintBackdropInPackage
            ? photoPrintBackground?.asset_image?.id ??
              printBackgrounds?.[0]?.asset_image?.id
            : printBackgrounds?.[0]?.asset_image?.id,
        });
      }
    };

    setBackground();
  }, [backgroundElement, photo]);

  const editablePhotoElements = useMemo(() => {
    const elements = getAccountCardElementsConfiguration(
      accountCard,
      cardElementLoading,
    );

    return (elements ?? []).filter(
      element =>
        element.cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
        element.side === CARD_SIDE.FRONT &&
        isCardElementEditable(element),
    );
  }, [accountCard, cardElementLoading]);

  useEffect(() => {
    const onFillEditableElements = async editableTextElements => {
      setPresetFillStatus(PRESET_FILL_STATUS.FILLING);

      if (editablePhotoElements?.length === 1 && photo) {
        const { cardElement, cardElementConfiguration } =
          editablePhotoElements[0] ?? {};

        // We don't want to replace the cropbox if there is an existing configuration.
        if (!cardElementConfiguration?.photo?.id) {
          const {
            width: cardWidth,
            height: cardHeight,
          } = getCardTemplateSizing(cardSides?.[0]?.card_template);
          await onElementConfigurationChange(editablePhotoElements[0], {
            photo_id: photo.id,
            is_editable: true,
            cropbox: getAutoFitPositionPhotoCropbox({
              cardElement,
              cardWidth,
              cardHeight,
              thumb: getMaxThumb(photo?.thumbnail_urls),
              faceboxes: (photo?.faces?.records ?? []).map(
                ({ facebox }) => facebox,
              ),
            }),
          });
        }
      }

      await pMap(editableTextElements, async element => {
        const { cardElementConfiguration } = element ?? {};
        const presetKey = Object.keys(TEXT_PRESETS).find(
          k => TEXT_PRESETS[k] === cardElementConfiguration?.help_placeholder,
        );
        // We only want to automatically update values where a preset is established.
        // Therefore, we ensure a matching key exists on our array.
        if (!cardElementConfiguration?.text && presetKey) {
          const text = getTextElementPresetData({
            presetLabel: TEXT_PRESETS[presetKey],
            identity,
            albumMembershipGroup,
            teamName,
            levelName,
          });
          if (text) {
            await onElementConfigurationChange(element, { text });
          } else {
            // Pass dumb text here. The opacity will hide it, but the dumb content
            // will make the system assume that the entity is "filled" for empty text
            // slot purposes.
            await onElementConfigurationChange(element, {
              text: 'NULL',
              opacity: 0,
              is_editable: false,
            });
          }
        }
      });

      setPresetFillStatus(PRESET_FILL_STATUS.FILLED);
    };

    if (
      presetFillStatus === PRESET_FILL_STATUS.LOADING &&
      accountCard &&
      !cardElementLoading
    ) {
      const editableTextElements = getAccountCardElementsConfiguration(
        accountCard,
        cardElementLoading,
      ).filter(
        element =>
          element.cardElement.type === CARD_ELEMENT_TYPE.TEXT &&
          element.side === CARD_SIDE.FRONT &&
          isCardElementEditable(element),
      );

      if (
        editableTextElements.length > 0 ||
        (editablePhotoElements?.length === 1 && photo)
      ) {
        onFillEditableElements(editableTextElements);
      } else {
        setPresetFillStatus(PRESET_FILL_STATUS.FILLED);
      }
    }
  }, [accountCard, cardElementLoading]);

  const onAddAccountCardElementReference = ({ cardElementId, ref }) => {
    setAccountCardElementReferences({
      ...accountCardElementReferences,
      [cardElementId]: ref,
    });
  };

  const classes = classNames({
    'is-loading': loadingAccountCard,
    [className]: !!className,
  });

  if (!printProduct) {
    return <DesignEditorLoadingIndicator />;
  }

  if (loadingAccountCard && !accountCard) {
    return (
      <div className={classes}>
        <NativeActivityIndicator />
      </div>
    );
  }

  return (
    <Fragment>
      {accountCard && presetFillStatus === PRESET_FILL_STATUS.FILLED && (
        <Fragment>
          <AccountCard
            className="print-product-configurator-account-card"
            allowOrientationChange
            onElementConfigurationChange={async (element, config) => {
              if (config?.orientation) {
                await handlePrintProductSizeSelect?.(
                  printProductSizeId,
                  config.orientation,
                );
              }
              await onElementConfigurationChange(element, config);
            }}
            onAddAccountCardElementReference={onAddAccountCardElementReference}
            accountCard={accountCard}
            cardElementLoading={cardElementLoading}
            photosContext={{
              identity_id: allowMultiIdentityPhoto ? undefined : identity?.id,
              album_id:
                albumMembershipGroup &&
                (albumMembershipGroup.is_album
                  ? albumMembershipGroup.id
                  : undefined),
              album_folder_id:
                albumMembershipGroup &&
                (!albumMembershipGroup.is_album
                  ? albumMembershipGroup.id
                  : undefined),
              root_album_folder_id:
                albumMembershipGroup && albumMembershipGroup.is_album
                  ? albumMembershipGroup.album.root_album_folder?.id
                  : undefined,
              is_album_membership_group: !!albumMembershipGroup,
              uploads_disabled: true,
              grid_select_disabled: true,
            }}
            allowPhotoChange={allowPhotoChange}
            defaultPhoto={photo}
            watermarkSettings={watermarkSettings}
          />
          <PrintBackgroundSelectorModal
            forceOpen={printBackgroundModalOpen}
            onClose={() => setPrintBackgroundModalOpen(false)}
            printBackgrounds={printBackgrounds}
            onPrintBackgroundSelect={async ({ printBackground }) => {
              if (printBackground.asset_image) {
                await onElementConfigurationChange(backgroundElement, {
                  photo_id: null,
                  // We need to keep the element non-editable for it to work properly.
                  is_editable: false,
                  existing_asset_image_id: printBackground?.asset_image?.id,
                });
              }
              setPrintBackgroundModalOpen(false);
            }}
          />
        </Fragment>
      )}
    </Fragment>
  );
};

PrintProductGroupConfigurationItem.propTypes = propTypes;

export default PrintProductGroupConfigurationItem;
